import { faPencil, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Icon } from 'src/components/Icon';
import { SearchParamsPagination } from 'src/components/Pagination';
import { StickyCard } from 'src/components/StickyCard';
import { DataTable } from 'src/components/Table/DataTable';
import { AssessmentSequence } from 'src/models/assessment/_types';
import {
  deleteAssessmentSequence,
  fetchAssessmentSequencesWithPagination,
} from 'src/models/assessment/assessment';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { SequenceFormDialog } from './Widgets/SequenceFormDialog';


export const AssessmentsSequencesPage = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessment.sequence',
  });
  const { t: tGlobal } = useTranslation('translation');
  const { t: tAssessment } = useTranslation('translation', {
    keyPrefix: 'assessment',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const { params, updateParam } = useListSearchParams({
    defaultPerPage: 10,
  });
  const queryClient = useQueryClient();
  const [confirmDeleteProps, confirm] = useConfirmDialogComponent();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSequence, setEditingSequence] =
    useState<AssessmentSequence | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['assessment-sequences', params.page, params.perPage],
    queryFn: () =>
      fetchAssessmentSequencesWithPagination({
        page: params.page || 1,
        perPage: params.perPage,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAssessmentSequence,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['assessment-sequences'],
      });
      toast.success(t('deletedSuccess'));
    },
    onError: (error: any) => {
      toast.error(api.formatError(error));
    },
  });

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSequence(null);
  };

  const handleCreateNew = () => {
    setEditingSequence(null);
    setDialogOpen(true);
  };
  const handleEditSequence = (sequence: AssessmentSequence) => {
    setEditingSequence(sequence);
    setDialogOpen(true);
  };
  const handleDelete = async (sequence: AssessmentSequence) => {
    if (
      await confirm({
        title: t('delete'),
        message: t('deleteSequenceConfirm'),
      })
    ) {
      deleteMutation.mutate({ sequenceId: sequence.id as string });
    }
  };

  const columns = [
    {
      id: 'name',
      label: tAssessment('one'),
    },
  ];
  const getSequenceName = (sequence: AssessmentSequence) => {
    return (
      sequence.name ||
      sequence.groupingItems?.map((item) => item.groupableName).join(', ') ||
      ''
    );
  };
  const actions = [
    {
      icon: faPencil,
      label: tCommon('actions.edit'),
      onClick: handleEditSequence,
    },
    {
      icon: faTrash,
      label: tCommon('actions.delete'),
      onClick: handleDelete,
    },
  ];
  return (
    <>
      <StickyCard
        title={t('title')}
        contentSx={{ p: 0 }}
        subtitle={t('info', {
          clients: tGlobal('trainer.client.other').toLowerCase(),
          assessments: tAssessment('plural').toLowerCase(),
        })}
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<Icon icon={faPlus} />}
            onClick={handleCreateNew}
          >
            {t('new')}
          </Button>
        }
      >
        <DataTable
          columns={columns}
          rows={(data?.sequences || []).map((seq) => ({
            ...seq,
            name: getSequenceName(seq),
          }))}
          onClickRow={(row) => handleEditSequence(row as AssessmentSequence)}
          noResultsText={t('noSequences')}
          loading={isLoading}
          rowActions={actions}
          fixedActions
        />

        <SearchParamsPagination
          params={params}
          updateParam={updateParam}
          total={data?.total || 0}
          itemsPerPage={params.perPage}
        />
      </StickyCard>
      {dialogOpen && (
        <SequenceFormDialog
          open={true}
          onClose={handleCloseDialog}
          editingSequence={editingSequence}
        />
      )}
      <ConfirmDialog {...confirmDeleteProps} />
    </>
  );
};
